import {
  IPlatformService,
  Platform,
  TVKeyMap,
  DeviceInfo,
  ScreenOrientation,
  OrientationChangeCallback,
} from '../types';
import { BROWSER_KEYS } from '../keys';
import { generateUUID } from '../../../utils/uuid';
import type {
  NavigatorWithBattery,
  DocumentWithVendorFullscreen,
  ElementWithVendorFullscreen,
} from '../dom-globals';

/** Forma mínima do WakeLockSentinel (não depende do lib.dom target). */
interface WakeLockSentinelLike {
  release(): Promise<void>;
  addEventListener?: (type: 'release', listener: () => void) => void;
}

export class BrowserPlatformService implements IPlatformService {
  readonly platform: Platform = 'browser';
  readonly keys: TVKeyMap = BROWSER_KEYS;
  private isInitialized = false;
  private orientationCallbacks: Set<OrientationChangeCallback> = new Set();
  private fullscreenCallbacks: Set<(isFullscreen: boolean) => void> = new Set();
  private currentOrientation: ScreenOrientation = 'landscape';

  init(): void {
    if (this.isInitialized) return;

    console.log('[BrowserPlatformService] Initializing...');
    this.isInitialized = true;
    console.log('[BrowserPlatformService] Running in web browser');

    // Setup orientation listener
    this.setupOrientationListener();
  }

  private setupOrientationListener(): void {
    // Detect initial orientation
    this.currentOrientation = this.detectOrientation();
    console.log(
      '[BrowserPlatformService] Initial orientation:',
      this.currentOrientation,
    );

    // Use Screen Orientation API if available
    if (screen.orientation) {
      screen.orientation.addEventListener(
        'change',
        this.handleOrientationChange,
      );
    } else {
      // Fallback: window resize
      window.addEventListener('resize', this.handleResize);
    }
  }

  private handleOrientationChange = (): void => {
    const newOrientation = this.detectOrientation();
    if (newOrientation !== this.currentOrientation) {
      console.log(
        '[BrowserPlatformService] Orientation changed:',
        this.currentOrientation,
        '->',
        newOrientation,
      );
      this.currentOrientation = newOrientation;
      this.notifyOrientationChange(newOrientation);
    }
  };

  private handleResize = (): void => {
    this.handleOrientationChange();
  };

  private detectOrientation(): ScreenOrientation {
    // Prioritize Screen Orientation API
    if (screen.orientation?.type) {
      return screen.orientation.type.includes('portrait')
        ? 'portrait'
        : 'landscape';
    }
    // Fallback: compare dimensions
    return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
  }

  private notifyOrientationChange(orientation: ScreenOrientation): void {
    this.orientationCallbacks.forEach((callback) => {
      try {
        callback(orientation);
      } catch (e) {
        console.error(
          '[BrowserPlatformService] Error in orientation callback:',
          e,
        );
      }
    });
  }

  exit(): void {
    // Browser cannot programmatically close windows/tabs
    console.log('[BrowserPlatformService] Exit not supported in browser');
  }

  /**
   * Restart remoto (PLAYER_RESTART_PLAN).
   * Em browser, 'refresh' = window.location.reload(). 'reboot_device' não tem
   * análogo (não dá pra reiniciar a máquina).
   */
  async restart(mode: 'refresh' | 'reboot_device'): Promise<void> {
    if (mode === 'reboot_device') {
      throw new Error('unsupported: reboot_device não disponível em browser');
    }
    console.log(
      '[BrowserPlatformService] Restart refresh — recarregando página',
    );
    window.location.reload();
  }

  onHide(): void {
    console.log('[BrowserPlatformService] Tab/window hidden');
  }

  onResume(): void {
    console.log('[BrowserPlatformService] Tab/window resumed');
  }

  isNativeEnvironment(): boolean {
    return false; // Browser is never "native" TV environment
  }

  canExit(): boolean {
    return false;
  }

  hasColorButtons(): boolean {
    return false;
  }

  hasMediaKeys(): boolean {
    return false;
  }

  /** Não é TV: tem toque ou mouse, e não sofre o limite de 1 decoder das TVs. */
  isTV(): boolean {
    return false;
  }

  /**
   * Detects the browser name from user agent.
   */
  private detectBrowserName(): string {
    const ua = navigator.userAgent;
    // Order matters: Chrome includes "Safari" in UA, so check Chrome first
    if (ua.includes('Edg/')) return 'EDGE';
    if (ua.includes('OPR/') || ua.includes('Opera')) return 'OPERA';
    if (ua.includes('Chrome')) return 'CHROME';
    if (ua.includes('Firefox')) return 'FIREFOX';
    if (ua.includes('Safari')) return 'SAFARI';
    return 'BROWSER';
  }

  /**
   * Returns a persistent device ID for browser.
   * Generates and stores a UUID in localStorage for consistent identification.
   */
  async getHardwareId(): Promise<string> {
    // Generate and persist a UUID for browser identification
    let browserId = localStorage.getItem('efflux_browser_hardware_id');
    if (!browserId) {
      const browserName = this.detectBrowserName();
      browserId = `${browserName}-${generateUUID().substring(0, 8).toUpperCase()}`;
      localStorage.setItem('efflux_browser_hardware_id', browserId);
    }
    console.log('[BrowserPlatformService] Using browser ID:', browserId);
    return browserId;
  }

  /**
   * Collects basic device information available in the browser.
   */
  /**
   * O browser não expõe RAM do sistema. `null` = não sei medir, que é diferente
   * de zero — e `performance.memory` NÃO serve aqui: é heap JS, o número que
   * ficou plano durante todo o vazamento que matava o embedded.
   */
  async getSystemMemoryMb(): Promise<{
    freeMb: number;
    totalMb: number | null;
  } | null> {
    return null;
  }

  async getDeviceInfo(): Promise<DeviceInfo> {
    const info: DeviceInfo = {
      platform: 'browser',
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      orientation: this.getOrientation(),
    };

    // Get browser name
    const browserName = this.detectBrowserName();
    info.model = `${browserName.charAt(0)}${browserName.slice(1).toLowerCase()} Browser`;

    // Try to get platform info
    info.platformVersion = navigator.platform || 'Unknown';
    info.manufacturer = 'Browser';

    // Try to get battery info via Web Battery API
    try {
      const getBattery = (navigator as NavigatorWithBattery).getBattery;
      if (getBattery) {
        const batteryManager = await getBattery.call(navigator);
        const level = batteryManager.level * 100;
        const isCharging = batteryManager.charging;

        info.battery = {
          level: Math.round(level),
          isCharging,
        };
        console.log(
          '[BrowserPlatformService] Battery info collected:',
          info.battery,
        );
      }
    } catch (e) {
      console.warn('[BrowserPlatformService] Battery API not supported:', e);
    }

    console.log('[BrowserPlatformService] Device info collected:', info);
    return info;
  }

  // Orientation methods

  /**
   * Browsers support orientation changes (especially on mobile/tablet).
   */
  supportsOrientationChange(): boolean {
    return true;
  }

  /**
   * Gets current display orientation.
   */
  getOrientation(): ScreenOrientation {
    const detected = this.detectOrientation();
    this.currentOrientation = detected;
    return detected;
  }

  /**
   * Registers a callback for orientation change events.
   * Returns an unsubscribe function.
   */
  onOrientationChange(callback: OrientationChangeCallback): () => void {
    this.orientationCallbacks.add(callback);
    return () => this.orientationCallbacks.delete(callback);
  }

  /**
   * Browser doesn't expose physical brightness by design (security/privacy).
   * Always returns null — UI should fall back to overlay brightness.
   */
  async getHardwareBrightness(): Promise<number | null> {
    return null;
  }

  /**
   * Browsers don't allow setting hardware brightness. Always throws.
   */
  async setHardwareBrightness(_level: number): Promise<void> {
    const err = new Error(
      'Hardware brightness unsupported in browser (security policy)',
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (err as any).code = 'unsupported';
    throw err;
  }

  // OTA Update methods

  /**
   * Install an OTA update bundle.
   * Browsers don't support native app installation.
   * This method throws an error with a helpful message.
   *
   * Alternative approach: Could implement service worker cache update
   * for Progressive Web Apps (PWA).
   *
   * @param bundlePath Local file path to the downloaded bundle
   * @returns Promise that rejects with error
   * @throws Error - Browsers cannot install native updates
   */
  async installUpdate(bundlePath: string): Promise<void> {
    console.warn(
      '[BrowserPlatformService] OTA installation not supported in browser:',
      bundlePath,
    );

    throw new Error(
      'OTA installation is not supported in browser environment. ' +
        'Browser apps are automatically updated when the server deploys a new version. ' +
        'Please refresh the page to get the latest version.',
    );

    // TODO: For PWA support, implement service worker cache update:
    // 1. Register service worker
    // 2. Cache new assets
    // 3. Clear old cache
    // 4. Reload to activate new version
    //
    // Example:
    // if ('serviceWorker' in navigator) {
    //   const registration = await navigator.serviceWorker.register('/sw.js');
    //   await registration.update();
    // }
  }

  /**
   * Restart the application after update.
   * On browsers, this performs a hard reload to bypass cache.
   */
  restartApp(): void {
    console.log(
      '[BrowserPlatformService] Restarting app (hard reload to bypass cache)...',
    );

    try {
      // Hard reload to bypass cache
      // location.reload(true) is deprecated, use cache: 'reload'
      window.location.reload();

      // For service worker apps, you might want to:
      // 1. Unregister service worker
      // 2. Clear caches
      // 3. Reload
      //
      // if ('serviceWorker' in navigator) {
      //   navigator.serviceWorker.getRegistrations().then(registrations => {
      //     registrations.forEach(registration => registration.unregister());
      //   });
      // }
    } catch (error) {
      console.error('[BrowserPlatformService] Failed to reload:', error);
      // Force reload anyway — assignment a window.location dispara navegação
      // mesmo quando .reload() falha (cenários CSP/iframe). Antes era um
      // self-assign de href que lint detectou como bug; o reload() padrão
      // já fecha a intenção.
      window.location.reload();
    }
  }

  /**
   * Get temporary directory for OTA downloads.
   * Browsers don't have traditional filesystem access.
   * This returns a placeholder path.
   *
   * For actual browser storage, use:
   * - IndexedDB for large binary data
   * - localStorage for small data
   * - Cache API for PWA assets
   *
   * @returns Placeholder temp directory path
   */
  getTempDirectory(): string {
    console.log(
      '[BrowserPlatformService] Browser does not have filesystem access',
    );

    // Return placeholder - actual browser storage would use IndexedDB or Cache API
    return '/tmp';
  }

  // Fullscreen methods

  /**
   * Browsers support fullscreen toggle via Fullscreen API.
   */
  supportsFullscreen(): boolean {
    return !!(
      document.fullscreenEnabled ||
      (document as DocumentWithVendorFullscreen).webkitFullscreenEnabled ||
      (document as DocumentWithVendorFullscreen).mozFullScreenEnabled ||
      (document as DocumentWithVendorFullscreen).msFullscreenEnabled
    );
  }

  /**
   * Checks if currently in fullscreen mode.
   */
  isFullscreen(): boolean {
    return !!(
      document.fullscreenElement ||
      (document as DocumentWithVendorFullscreen).webkitFullscreenElement ||
      (document as DocumentWithVendorFullscreen).mozFullScreenElement ||
      (document as DocumentWithVendorFullscreen).msFullscreenElement
    );
  }

  /**
   * Enters fullscreen mode for the specified element.
   * @param element Element to fullscreen (defaults to document.documentElement)
   */
  async enterFullscreen(element?: HTMLElement): Promise<void> {
    const el = element || document.documentElement;

    try {
      const elVendor = el as ElementWithVendorFullscreen;
      if (el.requestFullscreen) {
        await el.requestFullscreen();
      } else if (elVendor.webkitRequestFullscreen) {
        await elVendor.webkitRequestFullscreen();
      } else if (elVendor.mozRequestFullScreen) {
        await elVendor.mozRequestFullScreen();
      } else if (elVendor.msRequestFullscreen) {
        await elVendor.msRequestFullscreen();
      }
      console.log('[BrowserPlatformService] Entered fullscreen');
    } catch (error) {
      console.error(
        '[BrowserPlatformService] Failed to enter fullscreen:',
        error,
      );
      throw error;
    }
  }

  /**
   * Exits fullscreen mode.
   */
  async exitFullscreen(): Promise<void> {
    try {
      const docVendor = document as DocumentWithVendorFullscreen;
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (docVendor.webkitExitFullscreen) {
        await docVendor.webkitExitFullscreen();
      } else if (docVendor.mozCancelFullScreen) {
        await docVendor.mozCancelFullScreen();
      } else if (docVendor.msExitFullscreen) {
        await docVendor.msExitFullscreen();
      }
      console.log('[BrowserPlatformService] Exited fullscreen');
    } catch (error) {
      console.error(
        '[BrowserPlatformService] Failed to exit fullscreen:',
        error,
      );
      throw error;
    }
  }

  /**
   * Registers a callback for fullscreen change events.
   * Returns an unsubscribe function.
   */
  onFullscreenChange(callback: (isFullscreen: boolean) => void): () => void {
    this.fullscreenCallbacks.add(callback);

    const handleFullscreenChange = (): void => {
      const isFs = this.isFullscreen();
      this.fullscreenCallbacks.forEach((cb) => {
        try {
          cb(isFs);
        } catch (e) {
          console.error(
            '[BrowserPlatformService] Error in fullscreen callback:',
            e,
          );
        }
      });
    };

    // Add listeners for all vendor prefixes
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      this.fullscreenCallbacks.delete(callback);
      // Only remove listeners if no more callbacks
      if (this.fullscreenCallbacks.size === 0) {
        document.removeEventListener(
          'fullscreenchange',
          handleFullscreenChange,
        );
        document.removeEventListener(
          'webkitfullscreenchange',
          handleFullscreenChange,
        );
        document.removeEventListener(
          'mozfullscreenchange',
          handleFullscreenChange,
        );
        document.removeEventListener(
          'MSFullscreenChange',
          handleFullscreenChange,
        );
      }
    };
  }

  // Screen Wake Lock (V058) — mantém a tela acesa enquanto o player roda.
  // Honra a config `keepScreenAwake`. O sentinel cai quando a aba esconde,
  // então re-adquire no `visibilitychange`. No-op gracioso onde a API não
  // existe (Safari < 16.4) — a inibição nativa do <video> tocando cobre parte.

  private wakeLockSentinel: WakeLockSentinelLike | null = null;
  private wantsWakeLock = false;
  private wakeLockVisibilityBound = false;

  supportsWakeLock(): boolean {
    return typeof navigator !== 'undefined' && 'wakeLock' in navigator;
  }

  async requestScreenWakeLock(): Promise<void> {
    this.wantsWakeLock = true;
    this.bindWakeLockVisibility();
    await this.acquireWakeLock();
  }

  async releaseScreenWakeLock(): Promise<void> {
    this.wantsWakeLock = false;
    if (this.wakeLockSentinel) {
      try {
        await this.wakeLockSentinel.release();
      } catch (e) {
        console.warn('[BrowserPlatformService] wakeLock release failed:', e);
      }
      this.wakeLockSentinel = null;
    }
  }

  private acquireWakeLock = async (): Promise<void> => {
    if (!this.wantsWakeLock || !this.supportsWakeLock()) return;
    if (this.wakeLockSentinel) return; // já ativo
    try {
      const nav = navigator as Navigator & {
        wakeLock?: {
          request: (type: 'screen') => Promise<WakeLockSentinelLike>;
        };
      };
      const sentinel = await nav.wakeLock!.request('screen');
      this.wakeLockSentinel = sentinel;
      // Quando o SO libera (ex.: aba escondida), zera pra re-adquirir depois.
      sentinel.addEventListener?.('release', () => {
        this.wakeLockSentinel = null;
      });
      console.log('[BrowserPlatformService] Screen wake lock acquired');
    } catch (e) {
      // Renato 2026-06-12: o caso mais comum é "page not visible" — usuário
      // está em outra aba/janela. O browser bloqueia Wake Lock fora de
      // foreground (proteção contra abuse). O listener de visibilitychange
      // abaixo re-tenta automaticamente quando a aba volta a ser visível,
      // então não é erro — apenas um estado transitório. Rebaixa pra info
      // (cinza) pra não poluir console com warn vermelho.
      //
      // Outros casos (permissão revogada, política corporativa, navegador
      // sem suporte) ainda saem como warn — esses sim precisam atenção.
      const isPageNotVisible =
        e instanceof DOMException &&
        e.name === 'NotAllowedError' &&
        typeof document !== 'undefined' &&
        document.visibilityState !== 'visible';

      if (isPageNotVisible) {
        console.info(
          '[BrowserPlatformService] ⏸ Wake lock adiado — aba em background. Re-tenta quando visível.',
        );
      } else {
        console.warn('[BrowserPlatformService] wakeLock request failed:', e);
      }
    }
  };

  private bindWakeLockVisibility(): void {
    if (this.wakeLockVisibilityBound) return;
    this.wakeLockVisibilityBound = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void this.acquireWakeLock();
      }
    });
  }
}
